import { Security } from '../types';
import { resolveProviderId } from './dividendService';
import { toFmpSymbol } from './fmpDividendService';
import { getLatestQuoteFromAi } from './geminiService';
import { logTelemetry } from './telemetryService';
import { runWithMassiveRateLimit } from './massiveRateLimiter';
import { normaliseQuote } from '../utils/quoteNormalization';
import { getHistoricalByProviderKey, getQuoteByProviderKey } from './quoteService';

type Timespan = 'minute' | 'hour' | 'day' | 'week' | 'month';
export type MarketTimeframe = '1D' | '1W' | '1M' | '1Y' | 'All';

type CacheEntry<T> = { ts: number; data: T };

const getEnvValue = (key: string) =>
    import.meta.env?.[key] ??
    (typeof process !== 'undefined' ? process.env?.[key] : undefined);

const MARKET_DATA_ENDPOINT_BASE =
    (getEnvValue('VITE_MARKET_DATA_ENDPOINT_BASE_URL') as string | undefined) ||
    (getEnvValue('VITE_QUOTE_ENDPOINT_BASE_URL') as string | undefined);
const FMP_ENDPOINT_BASE =
    (getEnvValue('VITE_DIVIDEND_ENDPOINT_BASE_URL') as string | undefined) ||
    (getEnvValue('VITE_MARKET_DATA_ENDPOINT_BASE_URL') as string | undefined);
const EODHD_PLAN = (getEnvValue('VITE_EODHD_PLAN') as string | undefined) || 'free_extra_calls';
const FMP_PLAN = (getEnvValue('VITE_FMP_PLAN') as string | undefined) || 'free';
const FMP_FREE_SYMBOL_ALLOWLIST = new Set([
    'AAPL', 'TSLA', 'AMZN', 'MSFT', 'NVDA', 'GOOGL', 'META', 'NFLX', 'JPM', 'V', 'BAC', 'PYPL', 'DIS', 'T', 'PFE', 'COST', 'INTC', 'KO', 'TGT', 'NKE', 'SPY', 'BA', 'BABA', 'XOM', 'WMT', 'GE', 'CSCO', 'VZ', 'JNJ', 'CVX', 'PLTR', 'SQ', 'SHOP', 'SBUX', 'SOFI', 'HOOD', 'RBLX', 'SNAP', 'AMD', 'UBER', 'FDX', 'ABBV', 'ETSY', 'MRNA', 'LMT', 'GM', 'F', 'LCID', 'CCL', 'DAL', 'UAL', 'AAL', 'TSM', 'SONY', 'ET', 'MRO', 'COIN', 'RIVN', 'RIOT', 'CPRX', 'VWO', 'SPYG', 'NOK', 'ROKU', 'VIAC', 'ATVI', 'BIDU', 'DOCU', 'ZM', 'PINS', 'TLRY', 'WBA', 'MGM', 'NIO', 'C', 'GS', 'WFC', 'ADBE', 'PEP', 'UNH', 'CARR', 'HCA', 'TWTR', 'BILI', 'SIRI', 'FUBO', 'RKT',
]);

const latestCache = new Map<string, CacheEntry<number>>();
const historyCache = new Map<string, CacheEntry<{ date: Date; close: number }[]>>();
const quoteInflight = new Map<string, Promise<MarketQuote | null>>();

const TTL_LATEST_MS = 60 * 60 * 1000; // 1 hour
const TTL_HISTORY_MS = 60 * 60 * 1000; // 1 hour

const isFresh = (ts: number, ttlMs: number) => Date.now() - ts < ttlMs;

const buildEndpoint = (base: string | undefined, path: string, params?: Record<string, string>) => {
    const root = base?.replace(/\/$/, '') ?? '';
    if (!root) return null;
    const query = params ? new URLSearchParams(params).toString() : '';
    return `${root}/${path}${query ? `?${query}` : ''}`;
};

const hasMassiveEndpoint = () => Boolean(MARKET_DATA_ENDPOINT_BASE?.trim());



const toHistoryDateRange = (tf: MarketTimeframe) => {
    const { from, to } = timeframeParams(tf);
    return { from: formatYYYYMMDD(from), to: formatYYYYMMDD(to) };
};

const toEodhdHistoryProviderId = (providerId: string, security?: Security | null) => {
    const normalized = providerId.trim().toUpperCase();
    if (!normalized) return normalized;
    const base = normalized.replace(/\.LSE$/i, '').replace(/\.L$/i, '');
    const exg = (security?.exchange || '').toUpperCase();
    const isLse = exg.includes('LSE') || exg.includes('XLON') || exg.includes('LON') || (security?.currency || '').toUpperCase() === 'GBX';
    return isLse ? `${base}.LSE` : base;
};

export const fetchTimeframeSeriesByProviderIdNoMassive = async (
    providerId: string,
    timeframe: MarketTimeframe,
    opts?: { uid?: string | null; security?: Security | null }
): Promise<{ date: Date; close: number }[]> => {
    const { from, to } = toHistoryDateRange(timeframe);
    const normalizedProviderId = providerId.trim().toUpperCase();
    if (!normalizedProviderId) return [];

    const fmpProviderId = toFmpPlanSymbol(normalizedProviderId);
    const eodhdProviderId = toEodhdHistoryProviderId(normalizedProviderId, opts?.security);

    const chain: Array<{ source: 'fmp' | 'eodhd'; providerId: string }> = [
        { source: 'eodhd', providerId: eodhdProviderId },
        ...(canUseFmpForSymbol(fmpProviderId) ? [{ source: 'fmp', providerId: fmpProviderId }] : []),
    ];

    for (const item of chain) {
        try {
            const points = await getHistoricalByProviderKey(item.source, item.providerId, { from, to });
            if (points.length > 1) return points;
        } catch (error) {
            console.error('fetchTimeframeSeriesByProviderIdNoMassive source failed', {
                source: item.source,
                providerId: item.providerId,
                error,
            });
        }
    }

    return [];
};

const formatYYYYMMDD = (d: Date) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
};

const timeframeParams = (tf: MarketTimeframe): { from: Date; to: Date; multiplier: number; timespan: Timespan } => {
    const to = new Date();
    const from = new Date(to);

    switch (tf) {
        case '1D':
            from.setDate(to.getDate() - 1);
            return { from, to, multiplier: 30, timespan: 'minute' }; // 30-min bars
        case '1W':
            from.setDate(to.getDate() - 7);
            return { from, to, multiplier: 1, timespan: 'day' };
        case '1M':
            from.setDate(to.getDate() - 30);
            return { from, to, multiplier: 1, timespan: 'day' };
        case '1Y':
            from.setDate(to.getDate() - 365);
            return { from, to, multiplier: 1, timespan: 'week' };
        case 'All':
        default:
            from.setFullYear(to.getFullYear() - 5);
            return { from, to, multiplier: 1, timespan: 'month' };
    }
};

export type MarketQuote = {
    providerId: string;
    rawPrice: number;
    rawCurrency: string | null;
    price: number;
    previousClose?: number;
    change?: number;
    changePercent?: number;
    currency: string;
    asOf: string;
    fetchedAt?: string;
    unitScale?: 1 | 0.01;
    quoteUnit?: string | null;
    source?: string;
    canonicalQuoteKey?: string;
    diagnostics?: {
        providerPlan: string;
        providerSelected: 'eodhd' | 'fmp' | 'ai' | 'massive';
        fallbackUsed: boolean;
        fmpSkippedBecauseUnsupported: boolean;
        eodhdFeatureLimited: boolean;
    };
};

export const toFmpPlanSymbol = (symbolOrProviderId: string) =>
    String(symbolOrProviderId || '')
        .trim()
        .toUpperCase()
        .replace(/\.(US|LSE|L)$/i, '');

export const isFmpFreeSymbolSupported = (symbol: string) => FMP_FREE_SYMBOL_ALLOWLIST.has(toFmpPlanSymbol(symbol));

const canUseFmpForSymbol = (symbol: string) => (FMP_PLAN !== 'free' ? true : isFmpFreeSymbolSupported(symbol));

type MassiveFetchContext = {
    uid?: string | null;
    symbol?: string;
    category: 'QUOTE' | 'AGGS';
    meta?: Record<string, any>;
};

const fetchMassiveJson = async <T,>(inputUrl: string, ctx: MassiveFetchContext): Promise<T> => {
    const res = await fetch(inputUrl);

    if (!res.ok) {
        const body = await res.text().catch(() => '');
        try {
            await logTelemetry(ctx.uid, {
                provider: 'MASSIVE',
                category: ctx.category,
                level: res.status === 429 ? 'warn' : 'error',
                symbol: ctx.symbol,
                requestPath: inputUrl,
                status: res.status,
                message: `Massive failed: ${res.status} ${res.statusText}`,
                meta: {
                    ...ctx.meta,
                    responseSnippet: body.slice(0, 200),
                },
            });
        } catch (e) {
            console.warn('Telemetry logging failed', e);
        }
        throw new Error(`Massive ${res.status}: ${body.slice(0, 200)}`);
    }

    return (await res.json()) as T;
};

const ensureInstrumentKey = (params: Record<string, string>) => {
    if (params.ticker && !params.instrumentKey) {
        return { ...params, instrumentKey: params.ticker };
    }
    return params;
};

const massiveFetch = async (
    path: string,
    params: Record<string, string>,
    ctx: MassiveFetchContext
) => {
    const url = buildEndpoint(MARKET_DATA_ENDPOINT_BASE, path, ensureInstrumentKey(params));
    if (!url) {
        console.warn(
            'Massive endpoint base is not configured. Set VITE_MARKET_DATA_ENDPOINT_BASE_URL or VITE_QUOTE_ENDPOINT_BASE_URL.'
        );
        return null;
    }
    try {
        return await runWithMassiveRateLimit(() => fetchMassiveJson(url, ctx), {
            label: `${ctx.category}:${ctx.symbol ?? 'unknown'}`,
        });
    } catch (e) {
        console.error('massiveFetch failed', e);
        return null;
    }
};

const inferCurrency = (providerId: string, exchange?: string, fallback?: string) => {
    const explicitFallback = (fallback || '').trim().toUpperCase();
    if (explicitFallback) return explicitFallback;
    return providerId.endsWith('.L') ? 'GBP' : 'USD';
};

const inferExchange = (security: Security): string | undefined => {
    if (security.exchange) return security.exchange;
    const currency = (security.currency || '').toUpperCase();
    if (currency === 'GBX') return 'LSE';
    const isin = (security.isin || '').toUpperCase();
    if (isin.startsWith('GB')) return 'LSE';
    return undefined;
};

const normalizeFmpSymbol = (security: Security | null, fallbackSymbol: string) => {
    if (security?.symbol) {
        return toFmpSymbol(security.symbol, security.exchange).trim().toUpperCase();
    }
    return fallbackSymbol.toUpperCase();
};

const parseFmpTimestamp = (value?: number | string | null) => {
    const num = Number(value);
    if (!Number.isFinite(num)) return null;
    const ms = num < 1e12 ? num * 1000 : num;
    return new Date(ms).toISOString();
};

const fetchFmpQuote = async (
    symbol: string,
    ctx: { uid?: string | null; security?: Security | null; providerId?: string; fallbackCurrency?: string }
): Promise<MarketQuote | null> => {
    const normalizedSymbol = toFmpPlanSymbol(symbol);
    if (!canUseFmpForSymbol(normalizedSymbol)) return null;
    const url = buildEndpoint(FMP_ENDPOINT_BASE, 'getFmpQuote', { symbol: normalizedSymbol });
    if (!url) {
        console.warn(
            'FMP endpoint base is not configured. Set VITE_DIVIDEND_ENDPOINT_BASE_URL or VITE_MARKET_DATA_ENDPOINT_BASE_URL.'
        );
        return null;
    }
    try {
        const res = await fetch(url);
        if (!res.ok) {
            const body = await res.text().catch(() => '');
            try {
                await logTelemetry(ctx.uid, {
                    provider: 'FMP',
                    category: 'QUOTE',
                    level: res.status === 429 ? 'warn' : 'error',
                    symbol,
                    requestPath: 'getFmpQuote',
                    status: res.status,
                    message: `FMP failed: ${res.status} ${res.statusText}`,
                    meta: {
                        responseSnippet: body.slice(0, 200),
                    },
                });
            } catch (e) {
                console.warn('Telemetry logging failed', e);
            }
            return null;
        }

        const data = await res.json().catch(() => null);
        const row = Array.isArray(data) ? data[0] : data?.[0] || data;
        const price = Number(row?.price ?? row?.lastPrice ?? row?.close);
        if (!Number.isFinite(price)) return null;

        const asOf =
            parseFmpTimestamp(row?.timestamp) ||
            (typeof row?.date === 'string' ? new Date(row.date).toISOString() : null) ||
            new Date().toISOString();
        const fetchedAt = new Date().toISOString();
        const providerId = (row?.symbol || symbol).toUpperCase();
        const currency =
            row?.currency ||
            inferCurrency(providerId, ctx.security?.exchange, ctx.security?.currency || ctx.fallbackCurrency);
        const normalized = normaliseQuote({
            symbol: providerId,
            price,
            currency,
            currencySymbol: row?.currencySymbol,
            exchange: ctx.security?.exchange,
        });
        const previousClose = Number(row?.previousClose);
        const change = Number(row?.change);
        const changePercent = Number(row?.changePercentage ?? row?.changesPercentage ?? row?.changePercent);

        return {
            providerId,
            rawPrice: normalized.rawPrice,
            rawCurrency: normalized.rawCurrency,
            price: normalized.price,
            previousClose: Number.isFinite(previousClose) ? previousClose : undefined,
            change: Number.isFinite(change) ? change : undefined,
            changePercent: Number.isFinite(changePercent) ? changePercent : undefined,
            currency: normalized.currency || currency,
            asOf,
            fetchedAt,
            unitScale: normalized.unitScale,
            diagnostics: {
                providerPlan: `eodhd ${EODHD_PLAN} / fmp ${FMP_PLAN}`,
                providerSelected: 'fmp',
                fallbackUsed: true,
                fmpSkippedBecauseUnsupported: false,
                eodhdFeatureLimited: false,
            },
        };
    } catch (e) {
        console.error('FMP quote fetch failed', e);
        return null;
    }
};

const toEodhdProviderId = (providerId: string, security?: Security | null) => {
    const normalized = providerId.trim().toUpperCase();
    if (normalized.includes('.')) return normalized;
    const exchange = (security?.exchange || '').toUpperCase();
    if (exchange.includes('LSE') || exchange.includes('XLON') || exchange.includes('LON')) {
        return `${normalized}.LSE`;
    }
    return normalized;
};

const fetchEodhdQuote = async (
    providerId: string,
    ctx: { uid?: string | null; security?: Security | null; fmpSymbol?: string }
): Promise<MarketQuote | null> => {
    try {
        const eodhdProviderId = toEodhdProviderId(providerId, ctx.security);
        const quote = await getQuoteByProviderKey('eodhd', eodhdProviderId);
        return quote ? {
            ...quote,
            diagnostics: {
                providerPlan: `eodhd ${EODHD_PLAN} / fmp ${FMP_PLAN}`,
                providerSelected: 'eodhd',
                fallbackUsed: false,
                fmpSkippedBecauseUnsupported: FMP_PLAN === 'free' && !canUseFmpForSymbol(ctx.fmpSymbol || providerId),
                eodhdFeatureLimited: false,
            },
        } : null;
    } catch (e) {
        console.error('EODHD quote fetch failed', e);
        return null;
    }
};

const isLseSecurity = (providerId: string, security?: Security | null) => {
    if (providerId.endsWith('.L')) return true;
    const exchange = (security?.exchange || '').toUpperCase();
    if (exchange.includes('LSE') || exchange.includes('XLON') || exchange.includes('LON')) return true;
    const currency = (security?.currency || '').toUpperCase();
    if (currency === 'GBX') return true;
    return false;
};

const fetchAiQuote = async (
    symbol: string,
    ctx: { security?: Security | null; providerId?: string; fallbackCurrency?: string; asOfDate?: string }
): Promise<MarketQuote | null> => {
    const aiQuote = await getLatestQuoteFromAi(symbol, ctx.asOfDate);
    const price = Number(aiQuote?.price);
    if (!Number.isFinite(price)) return null;

    const providerId = (aiQuote?.symbol || symbol).toUpperCase();
    const currency = aiQuote?.currency || inferCurrency(providerId, ctx.security?.exchange, ctx.security?.currency || ctx.fallbackCurrency);
    const normalized = normaliseQuote({
        symbol: providerId,
        price,
        currency,
        exchange: ctx.security?.exchange,
    });
    const asOf = aiQuote?.asOf ? new Date(aiQuote.asOf).toISOString() : new Date().toISOString();
    const fetchedAt = new Date().toISOString();
    return {
        providerId,
        rawPrice: normalized.rawPrice,
        rawCurrency: normalized.rawCurrency,
        price: normalized.price,
        currency: normalized.currency || currency,
        asOf,
        fetchedAt,
        unitScale: normalized.unitScale,
    };
};

const normalizeSecurity = (security: Security): Security => {
    const inferredExchange = inferExchange(security);
    if (!inferredExchange || inferredExchange === security.exchange) return security;
    return { ...security, exchange: inferredExchange };
};

export const fetchLatestClose = async (security: Security): Promise<number | null> => {
    const normalizedSecurity = normalizeSecurity(security);
    const providerId = await resolveProviderId(normalizedSecurity);
    const fallbackSymbol = normalizedSecurity.symbol?.trim();
    if (!providerId && !fallbackSymbol) return null;

    return fetchLatestCloseByProviderId(providerId ?? fallbackSymbol, { security: normalizedSecurity });
};

export const fetchLatestCloseByProviderId = async (
    providerId: string,
    opts?: { uid?: string | null; security?: Security | null }
): Promise<number | null> => {
    const lseFallbackDate = isLseSecurity(providerId, opts?.security) ? formatYYYYMMDD(new Date()) : undefined;
    const cacheKey = providerId;
    const cached = latestCache.get(cacheKey);
    if (cached && isFresh(cached.ts, TTL_LATEST_MS)) return cached.data;

    if (!hasMassiveEndpoint()) {
        const fmpSymbol = normalizeFmpSymbol(opts?.security ?? null, providerId);
        if (lseFallbackDate) {
            const aiQuote = await fetchAiQuote(providerId, { security: opts?.security, asOfDate: lseFallbackDate });
            if (aiQuote) {
                latestCache.set(cacheKey, { ts: Date.now(), data: aiQuote.price });
                return aiQuote.price;
            }
        }
        const eodhdQuote = await fetchEodhdQuote(providerId, { uid: opts?.uid, security: opts?.security, fmpSymbol });
        if (eodhdQuote) {
            latestCache.set(cacheKey, { ts: Date.now(), data: eodhdQuote.price });
            return eodhdQuote.price;
        }
        const fmpQuote = await fetchFmpQuote(fmpSymbol, { uid: opts?.uid, security: opts?.security });
        if (!fmpQuote) {
            const aiQuote = await fetchAiQuote(providerId, { security: opts?.security, asOfDate: lseFallbackDate });
            if (!aiQuote) return null;
            latestCache.set(cacheKey, { ts: Date.now(), data: aiQuote.price });
            return aiQuote.price;
        }
        latestCache.set(cacheKey, { ts: Date.now(), data: fmpQuote.price });
        return fmpQuote.price;
    }

    try {
        const json = await runWithMassiveRateLimit(
            () =>
                massiveFetch('getMassiveAggsPrev', { ticker: providerId }, {
                    uid: opts?.uid,
                    symbol: providerId,
                    category: 'QUOTE',
                    meta: { dataProviderId: providerId },
                }),
            { label: 'latest-close' }
        );
        if (!json) {
            throw new Error('Massive proxy request failed.');
        }
        const close = Number(json?.results?.[0]?.c);
        if (!Number.isFinite(close)) {
            const fmpSymbol = normalizeFmpSymbol(opts?.security ?? null, providerId);
            if (lseFallbackDate) {
                const aiQuote = await fetchAiQuote(providerId, { security: opts?.security, asOfDate: lseFallbackDate });
                if (aiQuote) {
                    latestCache.set(cacheKey, { ts: Date.now(), data: aiQuote.price });
                    return aiQuote.price;
                }
            }
            const fmpQuote = await fetchFmpQuote(fmpSymbol, { uid: opts?.uid, security: opts?.security });
            if (!fmpQuote) {
                const eodhdQuote = await fetchEodhdQuote(providerId, { uid: opts?.uid, security: opts?.security, fmpSymbol });
                if (eodhdQuote) {
                    latestCache.set(cacheKey, { ts: Date.now(), data: eodhdQuote.price });
                    return eodhdQuote.price;
                }
                const aiQuote = await fetchAiQuote(providerId, { security: opts?.security, asOfDate: lseFallbackDate });
                if (!aiQuote) return null;
                latestCache.set(cacheKey, { ts: Date.now(), data: aiQuote.price });
                return aiQuote.price;
            }
            latestCache.set(cacheKey, { ts: Date.now(), data: fmpQuote.price });
            return fmpQuote.price;
        }

        latestCache.set(cacheKey, { ts: Date.now(), data: close });
        return close;
    } catch (e) {
        console.error('fetchLatestClose failed', e);
        const fmpSymbol = normalizeFmpSymbol(opts?.security ?? null, providerId);
        if (lseFallbackDate) {
            const aiQuote = await fetchAiQuote(providerId, { security: opts?.security, asOfDate: lseFallbackDate });
            if (aiQuote) {
                latestCache.set(cacheKey, { ts: Date.now(), data: aiQuote.price });
                return aiQuote.price;
            }
        }
        const fmpQuote = await fetchFmpQuote(fmpSymbol, { uid: opts?.uid, security: opts?.security });
        if (!fmpQuote) {
            const eodhdQuote = await fetchEodhdQuote(providerId, { uid: opts?.uid, security: opts?.security, fmpSymbol });
            if (eodhdQuote) {
                latestCache.set(cacheKey, { ts: Date.now(), data: eodhdQuote.price });
                return eodhdQuote.price;
            }
            const aiQuote = await fetchAiQuote(providerId, { security: opts?.security, asOfDate: lseFallbackDate });
            if (!aiQuote) return null;
            latestCache.set(cacheKey, { ts: Date.now(), data: aiQuote.price });
            return aiQuote.price;
        }
        latestCache.set(cacheKey, { ts: Date.now(), data: fmpQuote.price });
        return fmpQuote.price;
    }
};

export const fetchLatestQuote = async (
    securityOrProviderId: Security | string,
    opts?: { uid?: string | null }
): Promise<MarketQuote | null> => {
    const security = typeof securityOrProviderId === 'string' ? null : normalizeSecurity(securityOrProviderId);
    const providerId =
        typeof securityOrProviderId === 'string'
            ? securityOrProviderId
            : await resolveProviderId(security, { uid: opts?.uid });
    const fallbackSymbol = security?.symbol?.trim();
    if (!providerId && !fallbackSymbol) return null;
    const providerKey = providerId ?? fallbackSymbol!;
    const lseFallbackDate = isLseSecurity(providerKey, security) ? formatYYYYMMDD(new Date()) : undefined;

    try {
        const massiveQuote = hasMassiveEndpoint()
            ? await runWithMassiveRateLimit(async () => {
                  const json = await massiveFetch(
                      'getMassiveAggsPrev',
                      { ticker: providerKey },
                      {
                          uid: opts?.uid,
                          symbol: security?.symbol || providerKey,
                          category: 'QUOTE',
                          meta: { dataProviderId: providerKey },
                      }
                  );
                  if (!json) return null;
                  const close = Number(json?.results?.[0]?.c);
                  if (!Number.isFinite(close)) return null;
                  const asOf = json?.results?.[0]?.t ? new Date(Number(json.results[0].t)).toISOString() : new Date().toISOString();
                  const fetchedAt = new Date().toISOString();
                  const currency = inferCurrency(providerKey, security?.exchange, security?.currency);
                  const normalized = normaliseQuote({
                      symbol: providerKey,
                      price: close,
                      currency,
                      exchange: security?.exchange,
                  });
                  return {
                      providerId: providerKey,
                      rawPrice: normalized.rawPrice,
                      rawCurrency: normalized.rawCurrency,
                      price: normalized.price,
                      currency: normalized.currency || currency,
                      asOf,
                      fetchedAt,
                      unitScale: normalized.unitScale,
                  };
              }, { label: 'latest-quote' })
            : null;

        if (massiveQuote) return massiveQuote;

        const fmpSymbol = normalizeFmpSymbol(security, providerKey);
        if (lseFallbackDate) {
            const aiQuote = await fetchAiQuote(providerKey, { security, providerId: providerKey, asOfDate: lseFallbackDate });
            if (aiQuote) return aiQuote;
        }
        const eodhdQuote = await fetchEodhdQuote(providerKey, { uid: opts?.uid, security, fmpSymbol });
        if (eodhdQuote) return eodhdQuote;
        const fmpQuote = await fetchFmpQuote(fmpSymbol, { uid: opts?.uid, security, providerId: providerKey });
        if (fmpQuote) return fmpQuote;
        return await fetchAiQuote(providerKey, { security, providerId: providerKey, asOfDate: lseFallbackDate });
    } catch (e) {
        console.error('fetchLatestQuote failed', e);
        const fmpSymbol = normalizeFmpSymbol(security, providerKey);
        if (lseFallbackDate) {
            const aiQuote = await fetchAiQuote(providerKey, { security, providerId: providerKey, asOfDate: lseFallbackDate });
            if (aiQuote) return aiQuote;
        }
        const eodhdQuote = await fetchEodhdQuote(providerKey, { uid: opts?.uid, security, fmpSymbol });
        if (eodhdQuote) return eodhdQuote;
        const fmpQuote = await fetchFmpQuote(fmpSymbol, { uid: opts?.uid, security, providerId: providerKey });
        if (fmpQuote) return fmpQuote;
        return await fetchAiQuote(providerKey, { security, providerId: providerKey, asOfDate: lseFallbackDate });
    }
};

export async function fetchLatestQuoteCached(
    providerIdOrSymbol: string,
    opts?: { force?: boolean; uid?: string | null }
): Promise<MarketQuote | null> {
    const key = `quote:${providerIdOrSymbol}`;

    if (!opts?.force && quoteInflight.has(key)) {
        return quoteInflight.get(key)!;
    }

    const p = (async () => {
        try {
            return await fetchLatestQuote(providerIdOrSymbol, { uid: opts?.uid });
        } finally {
            quoteInflight.delete(key);
        }
    })();

    quoteInflight.set(key, p);
    return p;
}

export async function fetchLatestQuotesQueued(
    securities: Security[],
    onQuote: (quoteKey: string, q: MarketQuote) => void,
    opts?: { maxTotal?: number; force?: boolean; uid?: string | null }
): Promise<{ updated: number; attempted: number }> {
    const maxTotal = opts?.maxTotal ?? 25;

    const uniq = new Map<string, Security>();
    for (const sec of securities) {
        if (!sec?.symbol) continue;
        const normalized = normalizeSecurity(sec);
        const providerId = normalized.dataProviderId || (await resolveProviderId(normalized, { uid: opts?.uid }));
        if (!providerId) continue;
        uniq.set(providerId, { ...normalized, dataProviderId: providerId });
    }

    const list = Array.from(uniq.entries()).slice(0, maxTotal);
    let updated = 0;

    for (const [quoteKey] of list) {
        const q = await fetchLatestQuote(quoteKey, { uid: opts?.uid });
        if (q) {
            onQuote(quoteKey, q);
            updated += 1;
        }
    }

    return { updated, attempted: list.length };
}

export const fetchTimeframeSeries = async (
    security: Security,
    timeframe: MarketTimeframe,
    opts?: { uid?: string | null }
): Promise<{ date: Date; close: number }[]> => {
    if (!hasMassiveEndpoint()) {
        console.warn(
            'Massive endpoint base is not configured. Set VITE_MARKET_DATA_ENDPOINT_BASE_URL or VITE_QUOTE_ENDPOINT_BASE_URL.'
        );
        return [];
    }

    const providerId = await resolveProviderId(security, { uid: opts?.uid });
    if (!providerId) return [];

    return fetchTimeframeSeriesByProviderId(providerId, timeframe);
};

export const fetchTimeframeSeriesByProviderId = async (
    providerId: string,
    timeframe: MarketTimeframe,
    opts?: { uid?: string | null }
): Promise<{ date: Date; close: number }[]> => {
    if (!hasMassiveEndpoint()) {
        console.warn(
            'Massive endpoint base is not configured. Set VITE_MARKET_DATA_ENDPOINT_BASE_URL or VITE_QUOTE_ENDPOINT_BASE_URL.'
        );
        return [];
    }

    const { from, to, multiplier, timespan } = timeframeParams(timeframe);

    const cacheKey = `${providerId}|${multiplier}|${timespan}|${formatYYYYMMDD(from)}|${formatYYYYMMDD(to)}`;
    const cached = historyCache.get(cacheKey);
    if (cached && isFresh(cached.ts, TTL_HISTORY_MS)) return cached.data;

    try {
        const json = await runWithMassiveRateLimit(
            () =>
                massiveFetch(
                    'getMassiveAggsRange',
                    {
                        ticker: providerId,
                        multiplier: String(multiplier),
                        timespan,
                        from: formatYYYYMMDD(from),
                        to: formatYYYYMMDD(to),
                        adjusted: 'true',
                        sort: 'asc',
                        limit: '50000',
                    },
                    {
                        uid: opts?.uid,
                        symbol: providerId,
                        category: 'AGGS',
                        meta: { dataProviderId: providerId, timeframe },
                    }
                ),
            { label: 'timeframe-series' }
        );
        if (!json) {
            throw new Error('Massive proxy request failed.');
        }
        const results: any[] = Array.isArray(json?.results) ? json.results : [];

        const points = results
            .map((r) => ({
                date: new Date(Number(r.t)),
                close: Number(r.c),
            }))
            .filter((p) => Number.isFinite(p.close));

        historyCache.set(cacheKey, { ts: Date.now(), data: points });
        return points;
    } catch (e) {
        console.error('fetchTimeframeSeries failed', e);
        return [];
    }
};
